// Persona configuration — drives the 4 landing pages
// Slugs map to URL paths: /salaried, /self-employed, /business-owners, /property-investors

export const PERSONAS = {
  salaried: {
    slug: "salaried",
    eyebrow: "For Salaried Professionals",
    headline: "Smart loans calibrated to your payslip.",
    subhead:
      "Home loans, balance transfers and personal credit — without the bank-by-bank chase. We match your eligibility to the lender best suited for it.",
    image:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80",
    employmentDefault: "salaried",
    loanTypeDefault: "home_loan",
    benefits: [
      { t: "Best-fit lender, not the loudest", d: "We compare your profile across 15+ banks & NBFCs and pick what saves you the most over the tenure." },
      { t: "Transparent fees", d: "You only pay if you take the loan. No upfront charges, no hidden processing fees on our side." },
      { t: "Eligibility uplift", d: "We structure co-applicants, FOIR and NMI smartly — often unlocking 20-30% more eligibility than a single-bank check." },
      { t: "Speed", d: "Typical home loan sanction in 7–10 working days. Pre-approved offers possible for top corporates." },
    ],
    products: [
      { title: "Home Loan", desc: "Purchase, construction, plot+construction, top-up." },
      { title: "Balance Transfer", desc: "Move your existing home loan to a lower rate. Often saves ₹3–10 Lakh over 20 yrs." },
      { title: "Personal Loan", desc: "Wedding, medical, education or consolidation — instant pre-approvals from select banks." },
    ],
    faqs: [
      { q: "How much home loan can I get on my salary?", a: "Typically 60–72 months of net take-home, capped at FOIR ~55%. Your existing EMIs and credit score adjust this. We can run an eligibility check across all 15+ lenders in one go." },
      { q: "Will my CIBIL drop if multiple banks check me?", a: "It can — that's why we pre-vet your file once and submit only to the best-fit 1–2 lenders, instead of letting every bank pull your CIBIL independently." },
      { q: "Is balance transfer worth it?", a: "Usually yes if your current rate is 50+ bps higher than market AND you have 5+ years of tenure left. We'll do the math for you for free." },
    ],
    formCTA: "Get my home-loan eligibility",
  },
  "self-employed": {
    slug: "self-employed",
    eyebrow: "For Self-Employed & Professionals",
    headline: "Loans that respect your ITR and cash-flow.",
    subhead:
      "Doctors, CAs, consultants and small-firm owners — we know banks read your file differently. We package it the way they need to see it.",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80",
    employmentDefault: "self_employed",
    loanTypeDefault: "business_loan",
    benefits: [
      { t: "Read banks like a banker", d: "We know which lender accepts banking-based programs vs ITR-only — and submit your file accordingly." },
      { t: "Higher eligibility unlock", d: "We add back depreciation, director's salary and partner remuneration — often 30–50% more eligible loan than a self-applied case." },
      { t: "Cash-flow-aware structuring", d: "Right mix of CC, OD, term loan and LAP — so EMIs match your professional season, not the bank's calendar." },
      { t: "Single window for all needs", d: "Clinic setup, working capital, equipment, balance transfer — one advisor, one document set." },
    ],
    products: [
      { title: "Working Capital / OD / CC", desc: "Lines of credit secured against book-debts, GST or property." },
      { title: "Term Loans (Unsecured)", desc: "₹10L–₹2Cr at competitive rates for established professionals." },
      { title: "Loan Against Property", desc: "Unlock value from owned property — often the cheapest large-ticket route for self-employed." },
      { title: "Equipment / Machinery", desc: "Clinic setup, dental chairs, imaging, lab — structured against the asset." },
    ],
    faqs: [
      { q: "Banks rejected me — can you help?", a: "Most rejections we see come from how the file was packaged, not the underlying profile. Send us a 5-minute call and we'll tell you exactly what's fixable." },
      { q: "How many years of ITR do I need?", a: "Most banks need 2–3 years of ITR. Some NBFCs accept 12 months of GST returns or banking-only programs for newer practices/firms." },
      { q: "Can I get a loan as a sole proprietor?", a: "Yes — we routinely close sole-prop and partnership loans. The structure depends on whether the loan is in your individual name or the firm's." },
    ],
    formCTA: "Get my professional-loan plan",
  },
  "business-owners": {
    slug: "business-owners",
    eyebrow: "For Business Owners — MSME, Manufacturers, Distributors",
    headline: "Working capital that moves at the speed of your business.",
    subhead:
      "Manufacturers, wholesalers, distributors, dealers — we structure debt around your sales cycle, GST receipts and supplier terms. Targeted 48-hour approvals on eligible cases.",
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1200&q=80",
    employmentDefault: "business_owner",
    loanTypeDefault: "business_loan",
    benefits: [
      { t: "48hr approvals on eligible cases", d: "Pre-vetted file submission. Targeted turnaround on working capital and GST-program loans for clean books." },
      { t: "MSME schemes — used right", d: "CGTMSME, Mudra, Stand-Up India, Emergency Credit Line — we know which is actually disbursable, not just announced." },
      { t: "Channel & supply-chain finance", d: "Vendor finance, dealer finance, invoice/bill discounting — keep cash inside the business, not stuck in receivables." },
      { t: "Single advisor across the stack", d: "Working capital today, term loan next year, project finance after — one relationship that compounds." },
    ],
    products: [
      { title: "Working Capital (CC / OD)", desc: "₹25L+ secured against stock, debtors or property." },
      { title: "Unsecured Business Loans", desc: "GST-program & banking-program — ₹10L–₹2Cr, fast disbursal." },
      { title: "Machinery / Equipment Loans", desc: "Up to 90% funding on assets, structured tenure." },
      { title: "Channel & Vendor Finance", desc: "Anchor-led, invoice discounting, supply-chain finance." },
      { title: "MSME / Mudra / CGTMSME", desc: "We file under the right scheme so subsidies and guarantees actually come through." },
    ],
    faqs: [
      { q: "Minimum ticket size?", a: "₹25 Lakh for business loans is our sweet spot. We do smaller cases for existing relationships, but our pricing leverage kicks in above ₹25L." },
      { q: "How fast can I really get money?", a: "For clean GST-paying businesses with 3+ years of vintage and a 700+ promoter CIBIL — sanction in 48 hours, disbursal in 5–7 working days is realistic." },
      { q: "Will you work with my CA / accountant?", a: "Yes. We coordinate directly with your CA so document compilation doesn't become a bottleneck." },
    ],
    formCTA: "Get my working-capital plan",
  },
  "property-investors": {
    slug: "property-investors",
    eyebrow: "For Property Investors & Commercial Owners",
    headline: "Unlock the value sitting in your real estate.",
    subhead:
      "LAP, LRD, project finance — for IT parks, PG operators, rental landlords and commercial builders. Cheaper than unsecured debt, longer tenure, fewer covenants.",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80",
    employmentDefault: "property_investor",
    loanTypeDefault: "lap_lrd",
    benefits: [
      { t: "LRD against rental cash-flows", d: "Treat your rental income as a bond — borrow up to 70% of the present value of future rentals at 9–10%." },
      { t: "LAP without the document drama", d: "Right banks for legal opinion-friendly, gift-deeded and ancestral properties — we know who accepts what." },
      { t: "Project finance up to ₹100 Cr", d: "Construction-linked disbursement structured to your project schedule. IT parks, factories, warehouses, retail." },
      { t: "Tenure up to 15-20 years", d: "Lower EMIs than business loans, longer runway, asset stays with you." },
    ],
    products: [
      { title: "Loan Against Property (LAP)", desc: "₹50L–₹50Cr at 9–11% against residential or commercial property." },
      { title: "Lease Rental Discounting (LRD)", desc: "Borrow against confirmed lease rentals — banks, retailers, MNC tenants." },
      { title: "Project Finance", desc: "IT parks, factories, commercial construction, warehouses — funding linked to milestones." },
      { title: "Construction Finance", desc: "PG operators, residential builders, gated communities — funded against approved plans + collateral." },
    ],
    faqs: [
      { q: "How much LAP can I get on a Bangalore property?", a: "Typically 60–70% of fair market value, capped by your repayment capacity. We can give you an indicative number in one call if you share property details." },
      { q: "What's the difference between LAP and LRD?", a: "LAP is against the property's value. LRD is against the rental income from a leased property. LRD is cheaper but only available if you have a confirmed long-term tenant." },
      { q: "Can I get project finance as an individual?", a: "Yes, depending on the project and your track record. Most project finance is structured through a SPV/company though — we'll help you decide what's optimal." },
    ],
    formCTA: "Get my LAP / LRD plan",
  },
};

export const PERSONA_LIST = Object.values(PERSONAS);
